import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Car, Camera, Image as ImageIcon, Receipt, FileText, X, ChevronRight, ChevronLeft, ChevronDown,
  Shield, ShieldCheck, Wrench, ClipboardList, Pencil, MapPin, Calendar,
  CheckCircle, AlertTriangle, XCircle, MinusCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useFleetData } from '../hooks/useFleetData';
import { useOwner } from '../hooks/useOwner';
import { formatDate, formatCurrency, getExpiryStatus, formatMileage } from '../utils/dateUtils';
import { cropStyle } from '../utils/photoCrop';
import { Modal } from '../components/common/Modal';
import { ScanDocument } from '../components/common/ScanDocument';
import { ExpenseModal } from '../components/common/ExpenseModal';
import { VehicleForm, STATUS_CONFIG } from './Vehicles';
import { loadComparables, calcEstimatedValue, calcCollectorScore } from './ClassicCarIntelligence';
import { carPhotoFor } from '../utils/carPhotos';
import { latestByDate } from '../utils/recordUtils';
import { MakeBadge } from '../components/common/MakeBadge';

// The phone app's vehicle page: made for collectors, not office staff. One big photo, the
// value, a plain "is everything in order?" checklist, the photos, and a few large buttons.
// All the detail (costs, service history, restoration, reports …) is one tap away under
// "All records & documents", which opens the full page.

const TONE_ICON = { good: CheckCircle, warn: AlertTriangle, bad: XCircle, none: MinusCircle };

// Turn an expiry date into plain words for the checklist.
const expiryRow = (date, { exempt = false } = {}) => {
  if (!date && exempt) return { tone: 'ok', headline: 'Not needed', sub: 'While the car is immobilized' };
  if (!date) return { tone: 'none', headline: 'Not added yet', sub: 'Tap to add' };
  const s = getExpiryStatus(date);
  if (s.status === 'expired') return { tone: 'bad', headline: 'Expired', sub: `Ended ${formatDate(date)}` };
  if (['critical', 'warning', 'soon'].includes(s.status)) {
    const d = s.days;
    return { tone: 'warn', headline: d === 0 ? 'Due today' : `Due in ${d} day${d === 1 ? '' : 's'}`, sub: `Runs out ${formatDate(date)}` };
  }
  return { tone: 'good', headline: 'Up to date', sub: `Until ${formatDate(date)}` };
};

const docRow = (hasDoc) => hasDoc
  ? { tone: 'good', headline: 'On file', sub: 'Document saved' }
  : { tone: 'none', headline: 'Not added yet', sub: 'Tap to add' };

// Line icons drawn to match the design mock-up (engine block, speedometer).
const EngineIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 9h10" /><path d="M24 9v6" />
    <path d="M15 15h17l4 4h4v5h2v5h-2v4h-4l-4 4H18l-4-4v-4H9v-9h5v-2z" />
    <path d="M20 22h9" /><path d="M20 28h9" />
  </svg>
);
const SpeedIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8.5 34A18 18 0 1 1 39.5 34" />
    <path d="M24 11v3" /><path d="M11.5 18.5l2.4 1.8" /><path d="M36.5 18.5l-2.4 1.8" /><path d="M7 29h3" /><path d="M38 29h3" />
    <path d="M24 31l8-11" /><circle cx="24" cy="31" r="2.4" />
  </svg>
);

// Tiny value-over-time line for the Estimated Value card.
const Sparkline = ({ points, up }) => {
  const W = 112, H = 40, P = 4;
  const vs = points.map(p => p.v);
  const lo = Math.min(...vs), hi = Math.max(...vs);
  const span = hi - lo || 1;
  const ts = points.map(p => (p.t !== undefined ? p.t : (p.d ? p.d.getTime() : NaN)));
  const t0 = Math.min(...ts), t1 = Math.max(...ts);
  const byTime = ts.every(t => isFinite(t)) && t1 > t0;       // sales are not evenly spaced, so place them by date
  const xy = points.map((p, i) => [P + (byTime ? (ts[i] - t0) / (t1 - t0) : i / (points.length - 1)) * (W - P * 2), H - P - ((p.v - lo) / span) * (H - P * 2)]);
  const last = xy[xy.length - 1];
  const color = up ? 'var(--green)' : 'var(--red)';
  return (
    <svg className="cx-spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <polyline points={xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3.2" fill={color} />
    </svg>
  );
};

// The line on the Estimated Value card: how SOLD prices for this model have moved. Same sold results
// and the same smoothing as the "Previous Sales" chart on the Intelligence screen.
const soldTrendPoints = (comps) => {
  const pts = (comps || [])
    .filter(c => c.sold_price && Number(c.sold_price) > 0 && c._priceEUR > 0 && c.sale_date)
    .map(c => ({ t: new Date(c.sale_date).getTime(), v: c._priceEUR }))
    .filter(p => isFinite(p.t) && isFinite(p.v))
    .sort((a, b) => a.t - b.t);
  if (pts.length < 2) return null;
  let line = pts;
  if (pts.length >= 4) {
    const w = Math.max(3, Math.round(pts.length * 0.15));
    line = pts.map((pt, i) => {
      const a = Math.max(0, i - Math.floor(w / 2));
      const b = Math.min(pts.length, i + Math.ceil(w / 2));
      const slice = pts.slice(a, b);
      return { t: pt.t, v: slice.reduce((sum, p) => sum + p.v, 0) / slice.length };
    });
  }
  if (line.length > 30) {                                    // keep the little chart light
    const step = (line.length - 1) / 29;
    line = Array.from({ length: 30 }, (_, i) => line[Math.round(i * step)]);
  }
  return line;
};

// "+6.4% (12m)": how the value moved over the last year (or since the first entry when the
// history is shorter than that).
const valueChange = (history, current) => {
  const pts = history.map(h => ({ d: new Date(h.valuation_date), v: Number(h.value) })).filter(p => isFinite(p.v) && p.v > 0 && !isNaN(p.d));
  if (pts.length < 2) return { pts, pct: null, label: '' };
  const end = current && current > 0 ? current : pts[pts.length - 1].v;
  const yearAgo = Date.now() - 365 * 86400000;
  const ref = pts.find(p => p.d.getTime() >= yearAgo) || pts[0];
  const pct = ((end - ref.v) / ref.v) * 100;
  const months = Math.round((Date.now() - ref.d.getTime()) / (30.44 * 86400000));
  return { pts, pct, label: months >= 11 ? '(12m)' : `(${Math.max(1, months)}m)` };
};

export const VehiclePhone = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, isOwner, isAdmin } = useOwner();
  const canEdit = isOwner || isAdmin || role === 'editor';

  const [vehicle, setVehicle] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [history, setHistory] = useState([]);       // value over time, for the little chart
  const [docCount, setDocCount] = useState(0);
  const [statusOpen, setStatusOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [viewer, setViewer] = useState(-1);          // index of the photo open full-screen
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [scanModal, setScanModal] = useState(false);
  const [expModal, setExpModal] = useState(false);
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);

  const insurance = useFleetData('insurance_records', id);
  const roadTax = useFleetData('road_tax_records', id);
  const mechanical = useFleetData('mechanical_reports', id);
  const services = useFleetData('service_records', id);

  const load = useCallback(async () => {
    const { data: v } = await supabase.from('vehicles').select('*').eq('id', id).single();
    setVehicle(v || null);
    const { data: p } = await supabase.from('vehicle_photos').select('*').eq('vehicle_id', id).order('created_at', { ascending: false });
    setPhotos(p || []);
    const { data: val } = await supabase.from('vehicle_valuations').select('*').eq('vehicle_id', id).maybeSingle();
    setValuation(val || null);
    try {
      const { data: h } = await supabase.from('valuation_history').select('valuation_date, value').eq('vehicle_id', id).order('valuation_date', { ascending: true });
      setHistory(h || []);
      const [d, t] = await Promise.all([
        supabase.from('documents').select('id', { count: 'exact', head: true }).eq('vehicle_id', id),
        supabase.from('title_deeds').select('id', { count: 'exact', head: true }).eq('vehicle_id', id),
      ]);
      setDocCount((d.count || 0) + (t.count || 0));
    } catch { /* the chart and the counts are optional */ }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Sold-price trend for the Estimated Value card (undefined = still loading, null = not enough sales)
  const [soldTrend, setSoldTrend] = useState(undefined);
  const [comps, setComps] = useState(null);   // the scored comparables, same as the web Intelligence page uses
  useEffect(() => {
    if (!vehicle?.id || !vehicle.model) { setSoldTrend(null); return undefined; }
    let off = false;
    (async () => {
      try {
        const res = await loadComparables(vehicle);
        if (!off) { setSoldTrend(res.error ? null : soldTrendPoints(res.scored)); setComps(res.error ? null : res.scored); }
      } catch { if (!off) setSoldTrend(null); }
    })();
    return () => { off = true; };
  }, [vehicle?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setSheet(false);
    setUploading(true);
    let done = 0;
    const hadPhotos = photos.length > 0;
    for (const file of files) {
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('vehicle-photos').upload(path, file);
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from('vehicle-photos').getPublicUrl(path);
        const { error: insErr } = await supabase.from('vehicle_photos').insert({
          vehicle_id: id, url: publicUrl, file_path: path,
          is_primary: !hadPhotos && done === 0,           // the first photo of a car becomes its profile photo
          album: 'main',
        });
        if (insErr) throw insErr;
        done++;
      } catch (err) { toast.error(`Could not upload a photo: ${err.message}`); }
    }
    setUploading(false);
    if (done) { toast.success(done === 1 ? 'Photo added' : `${done} photos added`); load(); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!vehicle) return <div className="vp"><p className="vp-empty">We could not find this car.</p></div>;

  const hero = photos.find(p => p.is_primary) || photos[0];
  const studio = carPhotoFor(vehicle);                  // the studio picture, when this car has one
  const heroUrl = studio || hero?.url || null;       // the picture used as the page's background
  const isImmobilized = vehicle.is_immobilized || vehicle.status === 'immobilized';
  const isClassic = vehicle.status === 'classic';
  const statusLabel = STATUS_CONFIG[vehicle.status]?.label || '';
  // The same number as the web app's Intelligence page: a comparables-backed estimate wins, then the
  // owner's own figure, then whatever was last stored.
  let value = valuation?.current_value ? Number(valuation.current_value) : null;
  try {
    const est = comps ? calcEstimatedValue(comps, valuation ? calcCollectorScore(valuation) : null, valuation) : null;
    if (est?.estimatedValue != null && Number(est.estimatedValue) > 0) value = Number(est.estimatedValue);
    else if (valuation?.manual_market_value != null && Number(valuation.manual_market_value) > 0) value = Number(valuation.manual_market_value);
  } catch { /* keep the stored value */ }

  const checks = [
    { key: 'ins',  label: 'Insurance',     icon: Shield,        tab: 'insurance',  ...expiryRow(latestByDate(insurance.data, 'expiry_date')?.expiry_date) },
    { key: 'tax',  label: 'Road tax',      icon: Receipt,       tab: 'road-tax',   ...expiryRow(latestByDate(roadTax.data, 'expiry_date')?.expiry_date, { exempt: isImmobilized }) },
    { key: 'mot',  label: 'Inspection',    icon: ClipboardList, tab: 'mechanical', ...expiryRow(latestByDate(mechanical.data, 'next_due_date')?.next_due_date, { exempt: isImmobilized }) },
    { key: 'svc',  label: 'Next service',  icon: Wrench,        tab: 'services',   ...expiryRow(services.data[0]?.next_service_date) },
    ...(isClassic ? [
      { key: 'cid', label: 'Classic ID certificate', icon: FileText, tab: 'classic-id',    ...docRow(!!vehicle.classic_id_doc_url) },
      { key: 'imm', label: 'Immobilization document', icon: FileText, tab: 'immobilization', ...docRow(!!vehicle.immobilization_doc_url) },
    ] : []),
  ];
  const needAttention = checks.filter(c => ['bad', 'warn', 'none'].includes(c.tone)).length;
  const expired = checks.filter(c => c.tone === 'bad').length;
  const dueSoon = checks.filter(c => c.tone === 'warn').length;
  const missing = checks.filter(c => c.tone === 'none').length;
  const hasBad = expired > 0;
  // "All good", "All good • 1 due soon", "1 expired • 2 not added yet" …
  const statusLine = (() => {
    const bits = [];
    if (expired) bits.push(`${expired} expired`);
    if (dueSoon) bits.push(`${dueSoon} due soon`);
    if (missing) bits.push(`${missing} not added yet`);
    if (!expired && !missing) return ['All good', ...bits].join('  •  ');
    return bits.join('  •  ');
  })();
  const change = valueChange(history, value);
  const trendPct = soldTrend && soldTrend.length >= 2 ? ((soldTrend[soldTrend.length - 1].v - soldTrend[0].v) / soldTrend[0].v) * 100 : null;
  const shownPct = trendPct !== null ? trendPct : change.pct;
  const shownLabel = trendPct !== null ? '(sold trend)' : change.label;
  const graphPts = soldTrend === undefined ? [] : (soldTrend || change.pts);
  const records = (tab) => navigate(tab ? `/vehicles/${id}/records?tab=${tab}` : `/vehicles/${id}/records`);

  const shown = photos.slice(0, 10);

  return (
    <div className="vp cx-vp">
      {/* Top of the page: the car's picture is the background, with the buttons, badge and name on top */}
      <div className="cx-hero">
        {heroUrl && <img className="cx-hero-bg" src={heroUrl} alt="" aria-hidden="true" />}
        <div className="cx-hero-scrim" />
        {heroUrl
          ? (
            <button type="button" className="cx-hero-photo" onClick={() => photos.length && setViewer(Math.max(0, photos.indexOf(hero)))} aria-label="Open photos">
              <img src={heroUrl} alt={`${vehicle.brand} ${vehicle.model}`} style={studio ? undefined : cropStyle(hero)} />
            </button>
          )
          : <span className="cx-vp-nophoto"><Car size={64} /><span>No photo yet</span></span>}

        <div className="cx-vp-nav">
          <button className="cx-circle" aria-label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/vehicles'))}>
            <ChevronLeft size={22} />
          </button>
        </div>

        {/* Brand, model, year and registration */}
        <header className="cx-vp-head">
          <MakeBadge brand={vehicle.brand} size={46} />
          <div className="cx-vp-brand">{vehicle.brand}</div>
          <h1 className="cx-vp-model">{[vehicle.model, vehicle.variant].filter(Boolean).join(' ')}</h1>
          <div className="cx-vp-line">{[vehicle.year, vehicle.registration_number].filter(Boolean).join('  •  ')}</div>
        </header>
        {statusLabel && <span className="cx-vp-status cx-vp-status-top" style={{ '--dot': STATUS_CONFIG[vehicle.status]?.color || 'var(--accent)' }}>{statusLabel}</span>}
      </div>

      {/* Engine, mileage, year */}
      <div className="cx-vp-stats">
        {vehicle.engine_size && <div className="cx-stat"><EngineIcon size={32} /><div className="cx-stat-v">{vehicle.engine_size}</div><div className="cx-stat-k">Engine</div></div>}
        {vehicle.mileage ? <div className="cx-stat"><SpeedIcon size={32} /><div className="cx-stat-v">{formatMileage(vehicle.mileage, { both: false })}</div><div className="cx-stat-k">Mileage</div></div> : null}
        {vehicle.year ? <div className="cx-stat"><Calendar size={30} strokeWidth={1.5} /><div className="cx-stat-v">{vehicle.year}</div><div className="cx-stat-k">Year</div></div> : null}
      </div>

      {/* Estimated value */}
      <section className="cx-vcard cx-value">
        <div className="cx-value-main">
          <div className="cx-label">Estimated Value</div>
          <div className="cx-bignum">{value !== null ? `€${Math.round(value).toLocaleString('en-GB')}` : 'Not valued yet'}</div>
          {shownPct !== null && (
            <div className={`cx-change ${shownPct >= 0 ? 'is-up' : 'is-down'}`}>
              {shownPct >= 0 ? '+' : ''}{shownPct.toFixed(1)}% <span>{shownLabel}</span>
            </div>
          )}
        </div>
        {graphPts.length >= 2 && <Sparkline points={graphPts} up={shownPct === null || shownPct >= 0} />}
      </section>

      {/* Is everything in order? */}
      <section className="cx-vcard cx-status">
        <button type="button" className="cx-status-head" aria-expanded={statusOpen} onClick={() => setStatusOpen(o => !o)}>
          <span className="cx-status-icon">{needAttention ? <AlertTriangle size={24} /> : <ShieldCheck size={24} />}</span>
          <span className="cx-status-text">
            <span className="cx-status-title">Status Overview</span>
            <span className={`cx-status-sub tone-${hasBad ? 'bad' : (needAttention ? 'warn' : 'good')}`}>{statusLine}</span>
          </span>
          <ChevronDown size={22} className={`cx-status-chev${statusOpen ? ' is-open' : ''}`} />
        </button>
        {statusOpen && (
          <div className="vp-list cx-status-list">
            {checks.map(c => {
              const tone = c.tone === 'ok' ? 'none' : c.tone;
              const Tone = TONE_ICON[tone];
              return (
                <button key={c.key} className="vp-row" onClick={() => records(c.tab)}>
                  <span className="vp-row-icon"><c.icon size={22} /></span>
                  <span className="vp-row-text">
                    <span className="vp-row-label">{c.label}</span>
                    <span className="vp-row-sub">{c.sub}</span>
                  </span>
                  <span className={`vp-tag tone-${c.tone}`}><Tone size={17} />{c.headline}</span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Records, photos, documents, edit */}
      <div className={`cx-tiles${canEdit ? '' : ' is-three'}`}>
        <button className="cx-tile" onClick={() => records()}><ShieldCheck size={26} /><span>Records</span></button>
        <button className="cx-tile" onClick={() => records('photos')}><ImageIcon size={26} /><span>Photos{photos.length ? ` (${photos.length})` : ''}</span></button>
        <button className="cx-tile" onClick={() => navigate(`/documents?vehicle=${id}`)}><FileText size={26} /><span>Documents{docCount ? ` (${docCount})` : ''}</span></button>
        {canEdit && <button className="cx-tile" onClick={() => setEditing(true)}><Pencil size={26} /><span>Edit</span></button>}
      </div>

      {/* Photos */}
      {shown.length > 0 && (
        <section className="vp-section cx-vp-photos">
          <div className="vp-h2-row">
            <h2 className="vp-h2">Photos ({photos.length})</h2>
            <button className="vp-link" onClick={() => records('photos')}>See all</button>
          </div>
          <div className="vp-strip">
            {shown.map((p, i) => (
              <button key={p.id} className="vp-thumb" onClick={() => setViewer(i)} aria-label="Open photo">
                <img src={p.url} alt="" style={cropStyle(p)} />
              </button>
            ))}
          </div>
          {uploading && <div className="vp-uploading">Adding your photo…</div>}
        </section>
      )}
      {shown.length === 0 && uploading && <div className="vp-uploading" style={{ margin: '16px' }}>Adding your photo…</div>}

      {/* More */}
      <section className="vp-section">
        <div className="vp-list">
          {vehicle.location && (
            <div className="vp-row" style={{ cursor: 'default' }}>
              <span className="vp-row-icon"><MapPin size={22} /></span>
              <span className="vp-row-text"><span className="vp-row-label">Kept at</span><span className="vp-row-sub">{vehicle.location}</span></span>
            </div>
          )}
          <button className="vp-row" onClick={() => navigate(`/forms?vehicle=${id}`)}>
            <span className="vp-row-icon"><FileText size={22} /></span>
            <span className="vp-row-text"><span className="vp-row-label">Sale &amp; workshop forms</span><span className="vp-row-sub">Print or save as PDF</span></span>
            <ChevronRight size={22} className="vp-chev" />
          </button>
        </div>
      </section>

      {/* Camera button (this page only) */}
      {canEdit && createPortal(
        <button className="m-fab-float" aria-label="Add a photo, invoice or document" onClick={() => setSheet(true)}>
          <Camera size={28} />
        </button>,
        document.body
      )}

      {/* Hidden pickers */}
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={uploadPhotos} />
      <input ref={galleryInput} type="file" accept="image/*" multiple hidden onChange={uploadPhotos} />

      {/* What would you like to add? */}
      {sheet && createPortal(
        <div className="m-sheet-scrim" onClick={() => setSheet(false)}>
          <div className="m-sheet vp-sheet" onClick={e => e.stopPropagation()}>
            <div className="m-sheet-grip" />
            <div className="m-sheet-head">
              <div className="m-sheet-title">Add to this car</div>
              <button className="m-iconbtn" aria-label="Close" onClick={() => setSheet(false)}><X size={22} /></button>
            </div>
            {[
              { icon: Camera,    label: 'Take a photo',        hint: 'Use your camera',                          run: () => cameraInput.current?.click() },
              { icon: ImageIcon, label: 'Choose photos',       hint: 'Pick from your phone',                     run: () => galleryInput.current?.click() },
              { icon: Receipt,   label: 'Scan an invoice',     hint: 'Photograph a bill and save it as a cost', run: () => { setSheet(false); setExpModal(true); } },
              { icon: FileText,  label: 'Scan a document',     hint: 'Insurance, road tax, registration papers', run: () => { setSheet(false); setScanModal(true); } },
            ].map(a => (
              <button key={a.label} className="m-sheet-row" onClick={a.run}>
                <span className="m-sheet-icon"><a.icon size={24} /></span>
                <span className="m-sheet-text">
                  <span className="m-sheet-label">{a.label}</span>
                  <span className="m-sheet-hint">{a.hint}</span>
                </span>
                <ChevronRight size={20} className="m-sheet-chev" />
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Full-screen photo viewer */}
      {viewer >= 0 && photos[viewer] && createPortal(
        <div className="vp-viewer" onClick={() => setViewer(-1)}>
          <button className="vp-viewer-close" aria-label="Close" onClick={() => setViewer(-1)}><X size={28} /></button>
          <img src={photos[viewer].url} alt="" onClick={e => e.stopPropagation()} />
          {photos.length > 1 && (
            <>
              <button className="vp-viewer-nav is-prev" aria-label="Previous photo" onClick={e => { e.stopPropagation(); setViewer((viewer - 1 + photos.length) % photos.length); }}><ChevronLeft size={32} /></button>
              <button className="vp-viewer-nav is-next" aria-label="Next photo" onClick={e => { e.stopPropagation(); setViewer((viewer + 1) % photos.length); }}><ChevronRight size={32} /></button>
              <div className="vp-viewer-count">{viewer + 1} of {photos.length}</div>
            </>
          )}
        </div>,
        document.body
      )}

      {editing && (
        <Modal title="Edit car details" onClose={() => setEditing(false)} size="lg">
          <VehicleForm vehicle={vehicle} onSave={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />
        </Modal>
      )}
      {expModal && <ExpenseModal vehicleId={id} onClose={() => setExpModal(false)} />}
      {scanModal && (
        <ScanDocument
          vehicleId={id}
          vehicles={[vehicle]}
          onClose={() => setScanModal(false)}
          onSaved={() => { insurance.refetch(); roadTax.refetch(); mechanical.refetch(); services.refetch(); load(); }}
        />
      )}
    </div>
  );
};
